import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { FaFlag, FaTimes } from 'react-icons/fa';
import supabase from '../supabaseClient';
import { useAuth } from '../contexts/authContext';
import './ReportModal.css';

const REPORT_REASONS = [
  { value: 'harassment', label: 'Harassment or bullying' },
  { value: 'spam', label: 'Spam or scam' },
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'impersonation', label: 'Impersonation' },
  { value: 'hate_speech', label: 'Hate speech or discrimination' },
  { value: 'violence', label: 'Violence or threats' },
  { value: 'other', label: 'Other' },
];

const ReportModal = ({ isOpen, onClose, reportedUserId, reportedUserName, context = 'general' }) => {
  const { user } = useAuth();
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setDetails('');
      setSubmitted(false);
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason) {
      setError('Please select a reason for your report.');
      return;
    }
    if (!user?.id || !reportedUserId) {
      setError('Unable to submit report. Please try again.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const { error: insertError } = await supabase
        .from('user_reports')
        .insert({
          reporter_id: user.id,
          reported_user_id: reportedUserId,
          reason,
          details: details.trim() || null,
        });

      if (insertError) throw insertError;

      setSubmitted(true);
    } catch (err) {
      console.error('[ReportModal] Error submitting report:', err);
      setError('Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Report User"
      ariaHideApp={false}
      className="report-modal"
      overlayClassName="report-modal-overlay"
    >
      <div className="report-modal-header">
        <h2>
          <FaFlag /> Report User
        </h2>
        <button className="close-btn" onClick={handleClose} title="Close">
          <FaTimes />
        </button>
      </div>

      {submitted ? (
        <div className="report-modal-success">
          <div className="success-icon">✓</div>
          <h3>Report Submitted</h3>
          <p>Thank you for helping keep our community safe. We'll review your report and take appropriate action.</p>
          <button className="btn-primary" onClick={handleClose}>
            Close
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="report-modal-form">
          <p className="report-target">
            Reporting: <strong>{reportedUserName || 'User'}</strong>
          </p>

          <div className="form-group">
            <label htmlFor="report-reason">Reason for report *</label>
            <select
              id="report-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            >
              <option value="">Select a reason...</option>
              {REPORT_REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="report-details">Additional details (optional)</label>
            <textarea
              id="report-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Provide any additional context that might help us investigate..."
              rows={4}
              maxLength={1000}
            />
            <span className="char-count">{details.length}/1000</span>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="report-modal-actions">
            <button type="button" className="btn-secondary" onClick={handleClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn-primary btn-danger" disabled={submitting || !reason}>
              {submitting ? 'Submitting...' : 'Submit Report'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default ReportModal;
